import React, { createContext, useContext, useRef, useState, useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { api, logDev } from "@/lib/api";
import { attachBandwidthMonitor } from "@/lib/bandwidth";
import { useAuth } from "@/context/AuthContext";
const uid = () => Math.random().toString(36).slice(2, 10);
const VIDEO_WIDTH = 1280;
const VIDEO_HEIGHT = 720;
const ICE_GATHER_TIMEOUT_MS = 8000;
const ICE_RESTART_DELAY_MS = 4000;

const WS_URL = `${process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws")}/api/ws/match`;
const DEMO_REPLIES = ["Nice to meet you!", "That's interesting 🙂", "Where are you from?", "Tell me more", "Haha, same here!"];
export const REACTIONS = ["❤️", "😂", "👋", "🔥", "👍", "😮", "🎉", "😎"];
export const SPEECH_LANGS = { English: "en-US", Spanish: "es-ES", Portuguese: "pt-BR", French: "fr-FR", German: "de-DE", Hindi: "hi-IN", Arabic: "ar-SA", Japanese: "ja-JP", Korean: "ko-KR", Italian: "it-IT" };
const MAX_RECONNECT_ATTEMPTS = 8;
const CallContext = createContext(null);

export function CallProvider({ children }) {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const wsRef = useRef(null);
  const pcRef = useRef(null);
  const localRef = useRef(null);
  const remoteRef = useRef(null);
  const pendingIce = useRef([]);
  const iceServers = useRef([{ urls: ["stun:stun.l.google.com:19302", "stun:stun1.l.google.com:19302"] }]);
  const mutedRef = useRef(false);
  const camOffRef = useRef(false);
  const roleRef = useRef(null);
  const matchRef = useRef(null);
  const userRef = useRef(user);
  const callStartRef = useRef(null);
  const handlerRef = useRef(() => {});
  const intentionalClose = useRef(false);
  const reconnectAttempts = useRef(0);
  const reconnectTimer = useRef(null);
  const resumeTimer = useRef(null);
  const iceRestartTimer = useRef(null);
  const recognitionRef = useRef(null);
  const recognizingRef = useRef(false);

  const [status, setStatus] = useState("disconnected"); // disconnected | connecting | reconnecting | idle | queue | call
  const [online, setOnline] = useState(0);
  const [rtcConfig, setRtcConfig] = useState({ turn: false });
  const [match, setMatch] = useState(null); // { session_id, match, shared_interests, mode: "live" | "demo" }
  const [messages, setMessages] = useState([]);
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [rtcState, setRtcState] = useState("new");
  const [quality, setQuality] = useState("720p");
  const bwStop = useRef(null);
  const [partnerMedia, setPartnerMedia] = useState({ muted: false, cam_off: false });
  const [partnerAway, setPartnerAway] = useState(false);
  const [mediaError, setMediaError] = useState(null);
  const [muted, setMuted] = useState(false);
  const [camOff, setCamOff] = useState(false);
  const [lastEvent, setLastEvent] = useState(null);
  const [captionsOn, setCaptionsOn] = useState(false);
  const [partnerWantsCaptions, setPartnerWantsCaptions] = useState(false);
  const [captions, setCaptions] = useState([]); // { id, from: "me"|"them", text, translated, lang, final }
  const [reactions, setReactions] = useState([]); // { id, emoji, from, x }
  const [recap, setRecap] = useState(null); // { partner, shared, duration, mode }
  const [callStartedAt, setCallStartedAt] = useState(null);
  const [audioRecovering, setAudioRecovering] = useState(false);
  const [canFlip, setCanFlip] = useState(false);
  const facingRef = useRef("user");
  const recoveringRef = useRef(false);
  const statsRef = useRef({ bytes: 0, stalled: 0 });
  const [incomingCall, setIncomingCall] = useState(null); // { call_id, from }
  const [calling, setCalling] = useState(null); // { call_id, friend }
  const [friendsVersion, setFriendsVersion] = useState(0);
  const [captionsSupported] = useState(() => typeof window !== "undefined" && !!(window.SpeechRecognition || window.webkitSpeechRecognition));

  matchRef.current = match;
  userRef.current = user;
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;

  const send = useCallback((m) => {
    if (wsRef.current?.readyState === 1) wsRef.current.send(JSON.stringify(m));
  }, []);

  const applyMatch = useCallback((m) => { matchRef.current = m; setMatch(m); }, []);
  const markCallStart = useCallback(() => { callStartRef.current = Date.now(); setCallStartedAt(callStartRef.current); }, []);

  const closePeer = useCallback(() => {
    clearTimeout(iceRestartTimer.current);
    bwStop.current?.(); bwStop.current = null; setQuality("720p");
    if (pcRef.current) {
      pcRef.current.onicecandidate = null;
      pcRef.current.ontrack = null;
      pcRef.current.onconnectionstatechange = null;
      pcRef.current.close();
      pcRef.current = null;
    }
    pendingIce.current = [];
    remoteRef.current = null;
    setRemoteStream(null);
    setRtcState("new");
  }, []);

  const sendOffer = useCallback(async (pc, iceRestart = false) => {
    try {
      const offer = await pc.createOffer(iceRestart ? { iceRestart: true } : undefined);
      await pc.setLocalDescription(offer);
      send({ type: "signal", data: { sdp: pc.localDescription.toJSON() } });
    } catch (e) { logDev("offer failed", e); }
  }, [send]);

  const restartIce = useCallback(() => {
    const pc = pcRef.current;
    if (!pc || roleRef.current !== "caller" || pc.signalingState === "closed") return;
    try { pc.restartIce?.(); } catch (e) { logDev("ignored", e); }
    sendOffer(pc, true);
  }, [sendOffer]);

  const createPeer = useCallback(() => {
    closePeer();
    const pc = new RTCPeerConnection({ iceServers: iceServers.current });
    pcRef.current = pc;
    const s = localRef.current;
    if (s && s.getTracks().length) {
      s.getTracks().forEach((t) => pc.addTrack(t, s));
    } else {
      pc.addTransceiver("video", { direction: "recvonly" });
      pc.addTransceiver("audio", { direction: "recvonly" });
    }
    pc.onicecandidate = (e) => e.candidate && send({ type: "signal", data: { candidate: e.candidate.toJSON() } });
    pc.ontrack = (e) => {
      if (!remoteRef.current) remoteRef.current = e.streams[0] || new MediaStream();
      if (!e.streams[0] && !remoteRef.current.getTracks().includes(e.track)) remoteRef.current.addTrack(e.track);
      setRemoteStream(remoteRef.current);
    };
    bwStop.current?.();
    bwStop.current = attachBandwidthMonitor(pc, setQuality);
    pc.onconnectionstatechange = () => {
      const st = pc.connectionState;
      setRtcState(st);
      clearTimeout(iceRestartTimer.current);
      if (st === "failed") restartIce();
      else if (st === "disconnected") iceRestartTimer.current = setTimeout(() => { if (pcRef.current === pc && pc.connectionState !== "connected") restartIce(); }, ICE_RESTART_DELAY_MS);
    };
    return pc;
  }, [closePeer, send, restartIce]);

  const handleSignal = useCallback(async (data) => {
    const pc = pcRef.current;
    if (!pc || !data) return;
    if (data.request_restart) { if (roleRef.current === "caller") restartIce(); return; }
    try {
      if (data.sdp) {
        if (data.sdp.type === "offer" && pc.signalingState !== "stable") {
          await pc.setLocalDescription({ type: "rollback" }).catch(() => {});
        }
        await pc.setRemoteDescription(data.sdp);
        for (const c of pendingIce.current) await pc.addIceCandidate(c);
        pendingIce.current = [];
        if (data.sdp.type === "offer") {
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          send({ type: "signal", data: { sdp: pc.localDescription.toJSON() } });
        }
      } else if (data.candidate) {
        if (pc.remoteDescription) await pc.addIceCandidate(data.candidate);
        else pendingIce.current.push(data.candidate);
      }
    } catch (e) {
      logDev("signal error", e);
    }
  }, [send, restartIce]);

  // ---- Speech recognition (runs when someone in the call wants captions) ----
  const pushMyCaption = useCallback((text, final) => {
    setCaptions((list) => [...list.filter((c) => !(c.from === "me" && !c.final)), { id: `me-${Date.now()}`, from: "me", text, final, at: Date.now() }].slice(-8));
  }, []);

  const stopRecognition = useCallback(() => {
    recognizingRef.current = false;
    const r = recognitionRef.current;
    recognitionRef.current = null;
    if (r) { r.onend = null; try { r.stop(); } catch (e) { logDev("ignored", e); } }
  }, []);

  const startRecognition = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR || recognizingRef.current) return !!SR;
    const rec = new SR();
    rec.lang = SPEECH_LANGS[userRef.current?.languages?.[0]] || "en-US";
    rec.continuous = true;
    rec.interimResults = true;
    rec.onresult = (ev) => {
      let interim = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const r = ev.results[i];
        const text = r[0].transcript.trim();
        if (!text) continue;
        if (r.isFinal) {
          pushMyCaption(text, true);
          const m = matchRef.current;
          if (m?.mode === "live") send({ type: "caption", text, final: true });
          else if (m?.mode === "demo") {
            const target = m.match.primary_language || "English";
            api.post("/ai/translate", { text, target_lang: target }).then((res) => {
              if (res.data.translated) setCaptions((list) => [...list, { id: `demo-${Date.now()}`, from: "them", text, translated: res.data.translated, lang: "You", target_lang: target, final: true, at: Date.now() }].slice(-8));
            }).catch(() => {});
          }
        } else interim += `${text} `;
      }
      if (interim.trim()) {
        pushMyCaption(interim.trim(), false);
        if (matchRef.current?.mode === "live") send({ type: "caption", text: interim.trim(), final: false });
      }
    };
    rec.onerror = (e) => { if (e.error === "not-allowed" || e.error === "service-not-allowed") stopRecognition(); };
    rec.onend = () => { if (recognizingRef.current && recognitionRef.current === rec) { try { rec.start(); } catch (e) { logDev("ignored", e); } } };
    try { rec.start(); } catch { return false; }
    recognitionRef.current = rec;
    recognizingRef.current = true;
    return true;
  }, [pushMyCaption, send, stopRecognition]);

  const beginLiveCall = useCallback((m, role) => {
    roleRef.current = role;
    setPartnerAway(false);
    setPartnerMedia({ muted: false, cam_off: false });
    setStatus("call");
    const pc = createPeer();
    send({ type: "media", muted: mutedRef.current, cam_off: camOffRef.current });
    if (role === "caller") sendOffer(pc);
  }, [createPeer, send, sendOffer]);

  const freshCallState = useCallback(() => {
    setCaptions([]); setReactions([]); setCaptionsOn(false); setPartnerWantsCaptions(false); setRecap(null);
    stopRecognition();
  }, [stopRecognition]);

  const addReaction = useCallback((emoji, from) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    setReactions((r) => [...r, { id, emoji, from, x: 15 + Math.random() * 70 }].slice(-20));
    setTimeout(() => setReactions((r) => r.filter((x) => x.id !== id)), 2600);
  }, []);

  const handleMessage = useCallback(async (m) => {
    switch (m.type) {
      case "welcome":
        setOnline(m.online);
        if (reconnectAttempts.current > 0) {
          reconnectAttempts.current = 0;
          clearTimeout(resumeTimer.current);
          resumeTimer.current = setTimeout(() => {
            closePeer(); applyMatch(null); setMessages([]); freshCallState();
            if (pathnameRef.current === "/dashboard") { setStatus("idle"); return; }
            setStatus("queue"); send({ type: "join_queue" });
          }, 1500);
        } else setStatus((s) => (s === "connecting" ? "idle" : s));
        break;
      case "pong":
        setOnline(m.online); break;
      case "online":
        setOnline(m.count); break;
      case "queued":
        setStatus("queue"); setOnline(m.online); break;
      case "matched":
        setCalling(null); setIncomingCall(null);
        applyMatch({ session_id: m.session_id, match: m.partner, shared_interests: m.shared_interests || [], mode: "live" });
        setMessages([{ id: uid(), from: "system", text: `You're now connected with ${m.partner.name} from ${m.partner.country} ${m.partner.flag}` }]);
        freshCallState();
        markCallStart();
        beginLiveCall(m, m.role);
        break;
      case "resume":
        clearTimeout(resumeTimer.current);
        applyMatch({ session_id: m.session_id, match: m.partner, shared_interests: m.shared_interests || [], mode: "live" });
        setMessages((x) => [...x, { id: uid(), from: "system", text: "Reconnected — call restored." }]);
        beginLiveCall(m, m.role);
        break;
      case "partner_back":
        setPartnerAway(false);
        setMessages((x) => [...x, { id: uid(), from: "system", text: `${matchRef.current?.match?.name || "Partner"} is back.` }]);
        if (matchRef.current?.mode === "live") beginLiveCall(matchRef.current, roleRef.current);
        break;
      case "partner_reconnecting":
        setPartnerAway(true); break;
      case "demo_match":
        closePeer();
        roleRef.current = null;
        applyMatch({ session_id: m.session_id, match: { ...m.match, primary_language: m.match.languages?.[0] || "English" }, shared_interests: m.shared_interests || [], mode: "demo" });
        setMessages([{ id: uid(), from: "system", text: `Nobody else is online right now — this is a demo partner. ${m.match.name} will reply automatically.` }]);
        freshCallState();
        markCallStart();
        setPartnerAway(false);
        setPartnerMedia({ muted: false, cam_off: false });
        setStatus("call");
        break;
      case "signal":
        await handleSignal(m.data); break;
      case "chat":
        setMessages((x) => [...x, { id: uid(), from: "them", text: m.text }]); break;
      case "reaction":
        addReaction(m.emoji, "them"); break;
      case "media":
        setPartnerMedia({ muted: m.muted, cam_off: m.cam_off }); break;
      case "captions_state":
        setCaptionsOn(m.on);
        if (matchRef.current?.mode === "demo") { if (m.on) startRecognition(); else stopRecognition(); }
        if (!m.on) setCaptions([]);
        break;
      case "partner_captions":
        setPartnerWantsCaptions(m.on);
        if (m.on) startRecognition(); else stopRecognition();
        break;
      case "error":
        setLastEvent({ type: "error", code: m.code, message: m.message, at: Date.now() }); break;
      case "incoming_call":
        setIncomingCall({ call_id: m.call_id, from: m.from }); break;
      case "call_cancelled":
        setIncomingCall((c) => (c && c.call_id === m.call_id ? null : c));
        setLastEvent({ type: "call_cancelled", at: Date.now() });
        break;
      case "calling":
        setCalling({ call_id: m.call_id, friend: m.friend }); setStatus("calling"); break;
      case "call_failed":
        setCalling(null);
        setStatus((s) => (s === "calling" ? "idle" : s));
        setLastEvent({ type: "call_failed", reason: m.reason, at: Date.now() });
        break;
      case "friend_request":
      case "friend_accepted":
        setFriendsVersion((v) => v + 1);
        setLastEvent({ type: m.type, who: m.from || m.friend, at: Date.now() });
        break;
      case "friend_message":
        setFriendsVersion((v) => v + 1);
        setLastEvent({ type: "friend_message", who: m.from, text: m.text, at: Date.now() });
        break;
      case "streak":
        setLastEvent({ type: "streak", count: m.count, best: m.best, bonus: m.bonus, at: Date.now() }); break;
      case "moderation_notice":
        setLastEvent({ type: "moderation_notice", action: m.action, message: m.message, at: Date.now() }); break;
      case "caption":
        setCaptions((list) => {
          const idx = list.findIndex((c) => c.id === m.id);
          const entry = { id: m.id, from: "them", text: m.text, translated: m.translated, lang: m.lang, target_lang: m.target_lang, final: m.final, at: Date.now() };
          const next = idx >= 0 ? list.map((c, i) => (i === idx ? { ...c, ...entry } : c)) : [...list.filter((c) => c.final || c.from === "me"), entry];
          return next.slice(-8);
        });
        break;
      case "partner_left":
        setLastEvent({ type: "partner_left", reason: m.reason, at: Date.now() });
        closePeer();
        applyMatch(null);
        setMessages([]);
        freshCallState();
        setPartnerAway(false);
        setStatus("queue");
        send({ type: "join_queue" });
        break;
      default:
        break;
    }
  }, [addReaction, applyMatch, beginLiveCall, closePeer, freshCallState, handleSignal, markCallStart, send, startRecognition, stopRecognition]);
  handlerRef.current = handleMessage;

  const startLocalMedia = useCallback(async () => {
    if (localRef.current) return localRef.current;
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { width: { ideal: VIDEO_WIDTH }, height: { ideal: VIDEO_HEIGHT }, facingMode: facingRef.current }, audio: true });
      localRef.current = s;
      setLocalStream(s);
      setMediaError(null);
      navigator.mediaDevices.enumerateDevices().then((d) => setCanFlip(d.filter((x) => x.kind === "videoinput").length > 1)).catch(() => {});
      return s;
    } catch (e) {
      setMediaError(e?.name === "NotAllowedError" ? "Camera / mic permission denied — others won't see you." : "No camera or microphone found — joining view-only.");
      return null;
    }
  }, []);

  // ---- Audio recovery: reacquire a dead/ended mic track and swap it into the live sender ----
  const swapTrack = useCallback(async (kind, newTrack) => {
    const s = localRef.current || new MediaStream();
    s.getTracks().filter((t) => t.kind === kind).forEach((t) => { t.stop(); s.removeTrack(t); });
    s.addTrack(newTrack);
    localRef.current = s;
    setLocalStream(new MediaStream(s.getTracks()));
    const sender = pcRef.current?.getSenders().find((x) => x.track?.kind === kind || (!x.track && x.transceiver?.receiver?.track?.kind === kind));
    if (sender) await sender.replaceTrack(newTrack);
  }, []);

  const recoverAudio = useCallback(async (reason) => {
    if (recoveringRef.current) return;
    recoveringRef.current = true;
    setAudioRecovering(true);
    logDev("audio recovery:", reason);
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      const track = s.getAudioTracks()[0];
      track.enabled = !mutedRef.current;
      await swapTrack("audio", track);
      send({ type: "media", muted: mutedRef.current, cam_off: camOffRef.current });
    } catch (e) {
      logDev("audio recovery failed", e);
    } finally {
      recoveringRef.current = false;
      setTimeout(() => setAudioRecovering(false), 1500);
    }
  }, [send, swapTrack]);

  useEffect(() => {
    if (status !== "call" || match?.mode !== "live") { statsRef.current = { bytes: 0, stalled: 0 }; return undefined; }
    const tick = async () => {
      const pc = pcRef.current;
      const local = localRef.current?.getAudioTracks()[0];
      if (local && (local.readyState === "ended" || (local.muted && !mutedRef.current))) { statsRef.current.stalled = 0; recoverAudio(`local track ${local.readyState}/${local.muted ? "muted" : ""}`); return; }
      if (!pc || pc.connectionState !== "connected") return;
      try {
        let bytes = 0; let hasInbound = false;
        (await pc.getStats()).forEach((r) => { if (r.type === "inbound-rtp" && r.kind === "audio") { hasInbound = true; bytes += r.bytesReceived || 0; } });
        if (!hasInbound) return;
        if (bytes > statsRef.current.bytes) { statsRef.current = { bytes, stalled: 0 }; return; }
        statsRef.current.stalled += 1;
        if (statsRef.current.stalled === 3) {
          setAudioRecovering(true);
          if (roleRef.current === "caller") restartIce(); else send({ type: "signal", data: { request_restart: true } });
        }
        if (statsRef.current.stalled >= 6) { statsRef.current.stalled = 0; setAudioRecovering(false); }
      } catch (e) { logDev("ignored", e); }
    };
    const t = setInterval(tick, 2000);
    const onDev = () => { const l = localRef.current?.getAudioTracks()[0]; if (!l || l.readyState === "ended") recoverAudio("device change"); };
    navigator.mediaDevices?.addEventListener?.("devicechange", onDev);
    return () => { clearInterval(t); navigator.mediaDevices?.removeEventListener?.("devicechange", onDev); };
  }, [status, match?.mode, recoverAudio, restartIce, send]);

  useEffect(() => { if (rtcState === "connected" && statsRef.current.stalled === 0) setAudioRecovering(false); }, [rtcState]);

  const flipCamera = useCallback(async () => {
    const next = facingRef.current === "user" ? "environment" : "user";
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { exact: next }, width: { ideal: VIDEO_WIDTH }, height: { ideal: VIDEO_HEIGHT } } });
      const track = s.getVideoTracks()[0];
      track.enabled = !camOffRef.current;
      facingRef.current = next;
      await swapTrack("video", track);
      return true;
    } catch (e) {
      logDev("camera flip failed", e);
      return false;
    }
  }, [swapTrack]);

  const openSocket = useCallback((onOpen) => {
    const ws = new WebSocket(WS_URL);
    wsRef.current = ws;
    if (process.env.NODE_ENV !== "production") window.__orbitWs = ws;
    ws.onopen = () => onOpen?.();
    ws.onmessage = (ev) => { try { handlerRef.current(JSON.parse(ev.data)); } catch (e) { logDev("ignored", e); } };
    ws.onerror = () => {};
    ws.onclose = (ev) => {
      if (wsRef.current !== ws) return;
      wsRef.current = null;
      if (intentionalClose.current || [4001, 4002, 4003, 4005].includes(ev.code) || reconnectAttempts.current >= MAX_RECONNECT_ATTEMPTS) {
        setStatus("disconnected"); applyMatch(null); closePeer();
        return;
      }
      setStatus("reconnecting");
      const delay = Math.min(10000, 800 * 2 ** reconnectAttempts.current);
      reconnectAttempts.current += 1;
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = setTimeout(() => openSocket(), delay);
    };
  }, [applyMatch, closePeer]);

  const connect = useCallback(() => new Promise((resolve) => {
    if (wsRef.current && wsRef.current.readyState <= 1) return resolve();
    intentionalClose.current = false;
    reconnectAttempts.current = 0;
    setStatus("connecting");
    api.get("/rtc/config").then((r) => { iceServers.current = r.data.iceServers; setRtcConfig(r.data); }).catch(() => {});
    openSocket(() => { setStatus("idle"); resolve(); });
    setTimeout(resolve, ICE_GATHER_TIMEOUT_MS);
  }), [openSocket]);

  const resetCall = useCallback(() => { closePeer(); applyMatch(null); setMessages([]); setPartnerAway(false); freshCallState(); }, [closePeer, applyMatch, freshCallState]);
  const joinQueue = useCallback((prefer) => { resetCall(); setStatus("queue"); send(prefer ? { type: "join_queue", prefer } : { type: "join_queue" }); }, [resetCall, send]);
  const skip = useCallback(() => { resetCall(); setStatus("queue"); send({ type: "skip" }); }, [resetCall, send]);
  const leave = useCallback(() => { send({ type: "leave" }); resetCall(); setStatus("idle"); }, [resetCall, send]);

  const endCall = useCallback(() => {
    const m = matchRef.current;
    const me = userRef.current;
    const summary = m ? {
      partner: m.match, shared: m.shared_interests || [], mode: m.mode, session_id: m.session_id,
      duration: callStartRef.current ? Math.max(1, Math.round((Date.now() - callStartRef.current) / 1000)) : 0,
      my_country: me?.country, my_flag: me?.flag,
    } : null;
    send({ type: "leave" });
    resetCall();
    setStatus("idle");
    setRecap(summary);
  }, [resetCall, send]);

  const connectAgain = useCallback(() => {
    const prefer = recap?.mode === "live" ? recap.partner?.id : undefined;
    setRecap(null);
    joinQueue(prefer);
  }, [joinQueue, recap]);

  const callFriend = useCallback(async (friendId) => {
    await startLocalMedia();
    await connect();
    setRecap(null);
    setStatus("calling");
    send({ type: "call_friend", friend_id: friendId });
  }, [connect, send, startLocalMedia]);

  const acceptCall = useCallback(async () => {
    const c = incomingCall;
    if (!c) return;
    setIncomingCall(null);
    await startLocalMedia();
    setRecap(null);
    setStatus("calling");
    send({ type: "accept_call", call_id: c.call_id });
  }, [incomingCall, send, startLocalMedia]);

  const declineCall = useCallback(() => {
    if (incomingCall) send({ type: "decline_call", call_id: incomingCall.call_id });
    setIncomingCall(null);
  }, [incomingCall, send]);

  const cancelCall = useCallback(() => {
    send({ type: "cancel_call" });
    setCalling(null);
    setStatus("idle");
  }, [send]);

  const sendChat = useCallback((text) => {
    const t = text.trim();
    if (!t) return;
    setMessages((x) => [...x, { id: uid(), from: "me", text: t }]);
    if (matchRef.current?.mode === "live") send({ type: "chat", text: t });
    else setTimeout(() => setMessages((x) => [...x, { id: uid(), from: "them", text: DEMO_REPLIES[Math.floor(Math.random() * DEMO_REPLIES.length)] }]), 1200);
  }, [send]);

  const sendReaction = useCallback((emoji) => {
    addReaction(emoji, "me");
    if (matchRef.current?.mode === "live") send({ type: "reaction", emoji });
    else setTimeout(() => addReaction(REACTIONS[Math.floor(Math.random() * REACTIONS.length)], "them"), 900);
  }, [addReaction, send]);

  const toggleCaptions = useCallback(() => {
    if (captionsOn) send({ type: "captions_off" });
    else send({ type: "captions_on" });
  }, [captionsOn, send]);

  const toggleMic = useCallback(() => {
    const next = !mutedRef.current;
    mutedRef.current = next; setMuted(next);
    localRef.current?.getAudioTracks().forEach((t) => { t.enabled = !next; });
    send({ type: "media", muted: next, cam_off: camOffRef.current });
  }, [send]);

  const toggleCam = useCallback(() => {
    const next = !camOffRef.current;
    camOffRef.current = next; setCamOff(next);
    localRef.current?.getVideoTracks().forEach((t) => { t.enabled = !next; });
    send({ type: "media", muted: mutedRef.current, cam_off: next });
  }, [send]);

  const disconnectAll = useCallback(() => {
    intentionalClose.current = true;
    clearTimeout(reconnectTimer.current);
    clearTimeout(resumeTimer.current);
    reconnectAttempts.current = 0;
    const ws = wsRef.current;
    wsRef.current = null;
    if (ws) { try { ws.close(); } catch (e) { logDev("ignored", e); } }
    resetCall();
    setRecap(null);
    setCalling(null);
    setIncomingCall(null);
    localRef.current?.getTracks().forEach((t) => t.stop());
    localRef.current = null;
    setLocalStream(null); setStatus("disconnected");
  }, [resetCall]);

  const PRESENCE_PATHS = ["/match", "/chat", "/dashboard"];
  useEffect(() => {
    if (!PRESENCE_PATHS.includes(pathname) && status !== "disconnected") disconnectAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, status, disconnectAll]);

  // presence-only socket while on the dashboard (receive friend calls / requests)
  useEffect(() => {
    if (pathname === "/dashboard" && user && status === "disconnected") connect();
  }, [pathname, user, status, connect]);

  const value = {
    status, online, rtcConfig, match, messages, localStream, remoteStream, rtcState, quality, partnerMedia, partnerAway, mediaError, muted, camOff, lastEvent,
    captions, captionsOn, partnerWantsCaptions, captionsSupported, reactions, recap, callStartedAt, incomingCall, calling, friendsVersion, audioRecovering, canFlip,
    connect, startLocalMedia, joinQueue, skip, leave, endCall, connectAgain, sendChat, sendReaction, toggleMic, toggleCam, disconnectAll, send, toggleCaptions,
    callFriend, acceptCall, declineCall, cancelCall, flipCamera,
  };
  return <CallContext.Provider value={value}>{children}</CallContext.Provider>;
}

export const useCall = () => useContext(CallContext);
